import React from "react";
import styled from "styled-components";
import CoreLoop, { GAME_STATES, SUBSCRIPTIONS } from "../../CoreLoop";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import TimeRemaining from "../TimeRemaining/TimeRemaining";
import ScoreBackground from "../ScoreBackground/ScoreBackground";
import HiddenVideoAndImage from "../HiddenVideoAndImage/HiddenVideoAndImage";
import Results from "../Results/Results";
import TimeToCompletes from "../TimeToCompletes/TimeToCompletes";
import InitialInformation from "../InitialInformation";
import { ZUCK_PLUS_KEY } from "../../constants";
const Wrapper = styled.main`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
`;

const CanvasOuterContainer = styled.div`
  grid-area: overlap;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  max-width: 1800px;
  height: fit-content;
  align-self: center;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const CanvasInnerContainer = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: var(--opacity);
  pointer-events: none;
  transition: opacity 0.25s ease-in-out;
  position: relative;
  /* overflow: hidden; */
`;

const Canvas = styled.canvas`
  /* Maintain 3:4 aspect ratio */
  aspect-ratio: 3/4;
  --max-width-from-width: min(500px, calc(100vw - 32px));
  --max-height-from-height: min(
    600px,
    calc((100svh - 150px - 32px - 16px) / 2)
  );
  --max-width-from-height: calc(var(--max-height-from-height) * 3 / 4);
  --max-height-from-width: calc(var(--max-width-from-height) * 4 / 3);
  max-width: min(var(--max-width-from-width), var(--max-width-from-height));
  max-height: min(var(--max-height-from-width), var(--max-height-from-height));
  border-radius: 8px;

  /* Horizontal layout (desktop) */
  @media (min-width: 768px) {
    width: 100%;
    height: auto;
    --max-width-from-width: min(500px, calc(100vw - 32px - 16px) / 2);
    --max-height-from-height: min(600px, calc(100svh - 32px - 16px));
    --max-width-from-height: calc(var(--max-height-from-height) * 3 / 4);
    --max-height-from-width: calc(var(--max-width-from-height) * 4 / 3);
    max-width: min(var(--max-width-from-width), var(--max-width-from-height));
    max-height: min(
      var(--max-height-from-width),
      var(--max-height-from-height)
    );
  }
`;

const FlippedCanvas = styled(Canvas)`
  transform: scaleX(-1);
`;

const Playfield = styled.div`
  flex: 1;
  width: 100%;
  display: grid;
  grid-template-areas: "overlap";
  align-items: flex-start;
  justify-content: center;
`;

function App() {
  const videoRef = React.useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);
  const [isLandmarkerLoaded, setIsLandmarkerLoaded] = React.useState(false);
  const coreLoop = React.useRef(new CoreLoop());
  const id = React.useId();
  const videoCanvasRef = React.useRef(null);
  const imageCanvasRef = React.useRef(null);
  const imageRef = React.useRef(null);
  const [timeToCompletes, setTimeToCompletes] = React.useState([]);
  const [gameState, setGameState] = React.useState(GAME_STATES.FIRST_PAGE_LOAD);
  const [results, setResults] = React.useState(null);
  const [hasPurchasedZuckPlus, _setHasPurchasedZuckPlus] = React.useState(
    localStorage.getItem(ZUCK_PLUS_KEY) === "true"
  );

  const setHasPurchasedZuckPlus = React.useCallback(
    (value) => {
      _setHasPurchasedZuckPlus(value);
      localStorage.setItem(ZUCK_PLUS_KEY, value);
    },
    [_setHasPurchasedZuckPlus]
  );

  React.useEffect(() => {
    coreLoop.current.subscribe(SUBSCRIPTIONS.GAME_STATE_CHANGED, id, (data) => {
      setGameState(data);
    });
    coreLoop.current.subscribe(SUBSCRIPTIONS.RESULTS, id, (data) => {
      setResults(data);
    });
    coreLoop.current.subscribe(SUBSCRIPTIONS.LANDMARKER_LOADED, id, (data) => {
      setIsLandmarkerLoaded(data);
    });
    return () => {
      coreLoop.current.unsubscribe(SUBSCRIPTIONS.GAME_STATE_CHANGED, id);
      coreLoop.current.unsubscribe(SUBSCRIPTIONS.RESULTS, id);
      coreLoop.current.unsubscribe(SUBSCRIPTIONS.LANDMARKER_LOADED, id);
    };
  }, [id]);

  const startGame = React.useCallback(() => {
    // without this, we set "loading landmarkers" and then immediately do
    // some blocking stuff that prevents our UI from re-rendering in a loading state
    coreLoop.current.setGameState(GAME_STATES.LOADING_VIDEO);
    //
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        videoRef.current.srcObject = stream;
        const canvas = document.createElement("canvas");
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;

        const listener = () => {
          let interval = setInterval(() => {
            if (
              videoRef.current.videoWidth > 0 &&
              videoRef.current.videoHeight > 0 &&
              videoRef.current.playbackRate > 0 &&
              videoRef.current.paused === false &&
              videoRef.current.currentTime > 0
            ) {
              videoRef.current.removeEventListener("playing", listener);
              clearInterval(interval);
              setTimeout(() => {
                setIsVideoLoaded(true);
                coreLoop.current.loadLandmarkers();
              }, 500);
            }
          }, 100);
        };
        videoRef.current.addEventListener("playing", listener);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  React.useEffect(() => {
    coreLoop.current.subscribe(
      SUBSCRIPTIONS.COMPLETED,
      id,
      ({ success, timeToComplete }) => {
        console.log(timeToComplete);
        const now = performance.now();
        setTimeToCompletes((prev) => {
          const filtered = prev.filter((t) => now - t.time < 1000);
          return [...filtered, { time: now, timeToComplete, success }];
        });
      }
    );
    return () => {
      coreLoop.current.unsubscribe(SUBSCRIPTIONS.COMPLETED, id);
    };
  }, [id]);

  React.useEffect(() => {
    if (!videoCanvasRef.current || !imageCanvasRef.current) return;
    if (!isVideoLoaded || !isLandmarkerLoaded) return;

    coreLoop.current.runWebcamLoop({
      video: videoRef.current,
      videoCanvas: videoCanvasRef.current,
      image: imageRef.current,
      imageCanvas: imageCanvasRef.current,
    });
  }, [isVideoLoaded, isLandmarkerLoaded]);

  const canvasOpacity = React.useMemo(() => {
    if (gameState === GAME_STATES.WAITING_TO_BEGIN) return 0;
    else if (gameState === GAME_STATES.COUNTING_DOWN) return 1;
    else if (gameState === GAME_STATES.RUNNING_GAME) return 1;
    else if (gameState === GAME_STATES.COMPUTING_RESULTS) return 0;
    else if (gameState === GAME_STATES.SHOWING_RESULTS) return 0;
    else return 0;
  }, [gameState]);

  return (
    <Wrapper>
      <Header />
      <Playfield>
        <InitialInformation gameState={gameState} startGame={startGame} />
        <Results
          gameState={gameState}
          results={results}
          coreLoop={coreLoop}
          setHasPurchasedZuckPlus={setHasPurchasedZuckPlus}
          hasPurchasedZuckPlus={hasPurchasedZuckPlus}
        />
        <CanvasOuterContainer>
          <CanvasInnerContainer style={{ "--opacity": canvasOpacity }}>
            <Canvas width={600} height={800} ref={imageCanvasRef} />
          </CanvasInnerContainer>
          <CanvasInnerContainer style={{ "--opacity": canvasOpacity }}>
            <FlippedCanvas width={600} height={800} ref={videoCanvasRef} />
            {hasPurchasedZuckPlus && (
              <img
                src="/chain.png"
                alt="chain"
                style={{
                  position: "absolute",
                  top: "75%",
                  width: "100%",
                  height: "40%",
                  maxWidth: "450px",
                }}
              />
            )}
          </CanvasInnerContainer>
        </CanvasOuterContainer>
      </Playfield>
      <HiddenVideoAndImage videoRef={videoRef} imageRef={imageRef} />
      <ScoreBackground coreLoop={coreLoop} />
      <TimeToCompletes timeToCompletes={timeToCompletes} />
      <Footer />
      <TimeRemaining coreLoop={coreLoop} />
    </Wrapper>
  );
}

export default App;
